import { useMemo } from 'react';

import PropTypes from 'prop-types';

import { useError } from '#globalContexts/ErrorContext';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { useEmailCampaignDetailsContext } from '../../../contexts/EmailCampaignDetailsContext';

import CampaignStatsHeader from './components/CampaignStatsHeader';
import EmailReviewSplitScreen from './components/EmailReviewSplitScreen';
import GenerationProgress from './components/GenerationProgress';
import PendingContactsBanner from './components/PendingContactsBanner';
import ProductInfoCard from './components/ProductInfoCard';
import { useEmailGeneration } from './hooks/useEmailGeneration';
import { useEmailSelection } from './hooks/useEmailSelection';

const AICampaignReviewTab = ({ campaignId }) => {
	const { campaign } = useEmailCampaignDetailsContext();
	const signatureContent = campaign?.emailSignatures?.content || null;
	const { showError, showSuccess } = useError();

	// Email generation state + realtime-backed email list.
	const {
		emails,
		stats,
		progress,
		generatedCount,
		hasEmails,
		isGenerating,
		isLoading,
		refetchEmails,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		cancelMutation,
	} = useEmailGeneration(campaignId);

	const handleCancelGeneration = async () => {
		try {
			const result = await cancelMutation.mutateAsync();
			showSuccess({
				title: 'Generierung abgebrochen',
				message: result.message,
			});
		} catch (error) {
			showError({
				title: 'Fehler',
				error,
			});
		}
	};

	// Filter out approved emails if preference is set
	const hideApprovedEmails = usePreferencesStore((s) => s.hideApprovedEmails);
	const filteredEmails = useMemo(() => {
		if (!hideApprovedEmails) return emails;
		return emails.filter((e) => e.status !== 'approved');
	}, [emails, hideApprovedEmails]);

	// Use email selection hook (based on filtered list)
	const { selectedEmailId, setSelectedEmailId, selectedEmail } = useEmailSelection(filteredEmails);

	return (
		<div className="space-y-4">
			{/* Before emails are generated: Product Info Form (to enter the product description) - only show if no emails generated yet */}
			{!isLoading && !hasEmails && !isGenerating && <ProductInfoCard campaignId={campaignId} mode="setup" />}

			{/* Product Info Display/Edit (to edit the product description and trigger regeneration of the mails) - Show when emails exist */}
			{!isLoading && hasEmails && !isGenerating && <ProductInfoCard campaignId={campaignId} mode="display" />}

			{/* Pending contacts banner - Show when emails exist and not generating */}
			{!isLoading && hasEmails && !isGenerating && <PendingContactsBanner campaignId={campaignId} />}

			{/* Stats & Actions Header - Show when emails exist */}
			{hasEmails && <CampaignStatsHeader campaignId={campaignId} stats={stats} refetchEmails={refetchEmails} />}

			{/* Progress Bar with Cancel Button */}
			{isGenerating && (
				<GenerationProgress
					progress={progress}
					onCancel={handleCancelGeneration}
					isCancelling={cancelMutation.isPending}
					generatedCount={generatedCount}
				/>
			)}

			{/* Loading State */}
			{isLoading && !isGenerating && <div className="text-center py-8 text-bgBase-800">Laden...</div>}

			{/* After emails are generated: Review Generated Emails */}
			{!isLoading && hasEmails && (
				<EmailReviewSplitScreen
					campaignId={campaignId}
					emails={filteredEmails}
					selectedEmailId={selectedEmailId}
					setSelectedEmailId={setSelectedEmailId}
					selectedEmail={selectedEmail}
					refetchEmails={refetchEmails}
					signatureContent={signatureContent}
					emailAccountId={campaign?.emailAccountId}
					fetchNextPage={fetchNextPage}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
				/>
			)}
		</div>
	);
};

AICampaignReviewTab.propTypes = {
	campaignId: PropTypes.string.isRequired,
};

export default AICampaignReviewTab;
